import { map } from 'rxjs/operators';
import { Injector } from '@angular/core';
import { SgEntityFetchAdapter } from '../components/entity-select-auto-complete';
import { LegalEntitiesApi, LegalEntitiesApiClient } from '@sg/api/legal-entities-api';
import { validateArrayType } from './utils/validate-array';

declare module '../components/entity-select-auto-complete/entity-select-fetch-store.service' {
  export interface SgEntitySelectEntityFetchMap {
    AccountByIdMinorsOnly: {
      entity: LegalEntitiesApi.MinorAccountDto;
      query: LegalEntitiesApi.SearchLegalEntitiesAccountRequest;
    };
  }
}

export class AccountByIdMinorsOnlyToEntityFetcherLink implements SgEntityFetchAdapter<'AccountByIdMinorsOnly'> {
  readonly key: 'AccountByIdMinorsOnly' = 'AccountByIdMinorsOnly';
  readonly metadata: {
    idField: keyof LegalEntitiesApi.MinorAccountDto;
    labelField: Array<string>;
    queryField?: { field: keyof LegalEntitiesApi.SearchLegalEntitiesAccountRequest; isArray?: boolean };
  } = {
    idField: 'accountId',
    labelField: ['${accountName}', '(${entityAccountId})'],
    queryField: { field: 'accountNames', isArray: true },
  };

  private readonly legalEntitiesApi: LegalEntitiesApiClient;

  constructor(injector: Injector) {
    this.legalEntitiesApi = injector.get(LegalEntitiesApiClient);
  }

  getEntity(accountIds: string[]) {
    const body: LegalEntitiesApi.SearchLegalEntitiesAccountRequest = { accountIds: validateArrayType(accountIds) };
    return this.legalEntitiesApi
      .legalEntitiesControllerGetLegalEntitiesAccountsBySearchParams({ body, pageNumber: 1 }, { observe: 'body' })
      .pipe(
        map((results) =>
          results.rows
            .filter(
              (row): row is LegalEntitiesApi.MinorAccountResponse =>
                row.type === LegalEntitiesApi.AccountTypeEnum.minorAccount
            )
            .map((row) => row.account)
            .filter((account): account is LegalEntitiesApi.MinorAccountDto => account !== undefined)
        )
      );
  }

  findEntities(query?: LegalEntitiesApi.SearchLegalEntitiesAccountRequest, pageNumber?: number) {
    const body: LegalEntitiesApi.SearchLegalEntitiesAccountRequest = {};

    if (query) {
      Object.assign(body, query);
    }

    return this.legalEntitiesApi
      .legalEntitiesControllerGetLegalEntitiesAccountsBySearchParams(
        { body, pageSize: 10, pageNumber: pageNumber ?? 1 },
        { observe: 'body' }
      )
      .pipe(
        map((result) => {
          const minorAccounts = result.rows
            ? result.rows
                .filter(
                  (row): row is LegalEntitiesApi.MinorAccountResponse =>
                    row.type === LegalEntitiesApi.AccountTypeEnum.minorAccount
                )
                .map((row) => row.account)
                .filter((account): account is LegalEntitiesApi.MinorAccountDto => account !== undefined)
            : [];

          return {
            data: minorAccounts,
            infiniteScrollInfo: { currentPage: result.paging.pageNumber, totalPages: result.paging.totalPages },
          };
        })
      );
  }
}
